import { ApiError } from '@/services/api';
import { feedService } from '@/services/feedService';
import type { FeedCtaAction } from '@/types/feed';

/**
 * Feed CTA taps for registration attribution (epic 21 #1970). Every tap is parked here first and
 * sent when the viewer is logged in — straight away, or right after login when the tap was what
 * sent them to log in. One entry (the latest tap); it is only worth sending within the API's 24 h
 * attribution window.
 */
export const PENDING_FEED_TAP_KEY = 'feed.pendingCtaTap.v1';
export const PENDING_FEED_TAP_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface PendingFeedTap {
  postId: string;
  action: FeedCtaAction;
  tappedAtMs: number;
}

const read = (): PendingFeedTap | null => {
  try {
    const raw = localStorage.getItem(PENDING_FEED_TAP_KEY);
    return raw ? (JSON.parse(raw) as PendingFeedTap) : null;
  } catch {
    return null;
  }
};

const clear = () => {
  try {
    localStorage.removeItem(PENDING_FEED_TAP_KEY);
  } catch {
    /* nothing to clear */
  }
};

export function parkFeedTap(
  postId: string,
  action: FeedCtaAction,
  nowMs = Date.now(),
): void {
  try {
    localStorage.setItem(
      PENDING_FEED_TAP_KEY,
      JSON.stringify({
        postId,
        action,
        tappedAtMs: nowMs,
      } satisfies PendingFeedTap),
    );
  } catch {
    /* storage blocked: the tap just isn't attributed */
  }
}

let inFlight: Promise<void> | null = null;

/**
 * Send the parked tap if there is a fresh one. Call only when logged in. Never throws: attribution
 * must not break a CTA. A network failure or a 429 keeps the tap for the next try; any other server
 * answer (recorded, 404 removed post, 4xx) clears it.
 */
export function flushPendingFeedTap(nowMs = Date.now()): Promise<void> {
  if (inFlight) return inFlight;
  const tap = read();
  if (!tap) return Promise.resolve();
  if (
    typeof tap.postId !== 'string' ||
    nowMs - tap.tappedAtMs > PENDING_FEED_TAP_MAX_AGE_MS
  ) {
    clear();
    return Promise.resolve();
  }
  inFlight = feedService
    .recordCtaTap(tap.postId, tap.action)
    .then(clear, (error: unknown) => {
      const retryable =
        !(error instanceof ApiError) ||
        error.isNetworkError ||
        error.status === 429;
      if (!retryable) clear();
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
